import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, MouseEvent, SetStateAction } from 'react';
import type { CcontainerProps } from './Ccontainer.types';

const MIN_PRESS_VISIBLE_MS = 180;

interface UseContainerPressInteractionInput {
  onClick?: CcontainerProps['onClick'];
  clickAnimation: boolean;
}

export interface ContainerInteractionHandlers {
  onTouchStart: () => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseUp: () => void;
}

export interface ContainerPressInteraction {
  isHovered: boolean;
  isPressed: boolean;
  interactionHandlers: ContainerInteractionHandlers;
}

interface PressResetControls {
  beginPress: () => void;
  releasePress: () => void;
  clearPendingPressReset: () => void;
}

interface UseContainerInteractionHandlersInput extends PressResetControls {
  onClick?: CcontainerProps['onClick'];
  clickAnimation: boolean;
  isTouchDevice: boolean;
  setIsTouchDevice: Dispatch<SetStateAction<boolean>>;
  setIsHovered: Dispatch<SetStateAction<boolean>>;
  setIsPressed: Dispatch<SetStateAction<boolean>>;
}

type ContainerTouchHandlers = Pick<
  ContainerInteractionHandlers,
  'onTouchStart' | 'onTouchEnd' | 'onTouchCancel'
>;
type ContainerMouseHandlers = Pick<
  ContainerInteractionHandlers,
  'onMouseEnter' | 'onMouseLeave' | 'onMouseDown' | 'onMouseUp'
>;

export function useContainerPressInteraction(input: UseContainerPressInteractionInput): ContainerPressInteraction {
  const { onClick, clickAnimation } = input;
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const { beginPress, releasePress, clearPendingPressReset } = usePressResetControls({
    setIsHovered,
    setIsPressed,
  });
  const interactionHandlers = useContainerInteractionHandlers({
    onClick,
    clickAnimation,
    isTouchDevice,
    setIsTouchDevice,
    setIsHovered,
    setIsPressed,
    beginPress,
    releasePress,
    clearPendingPressReset,
  });

  return {
    isHovered,
    isPressed,
    interactionHandlers,
  };
}

function usePressResetControls(input: {
  setIsHovered: Dispatch<SetStateAction<boolean>>;
  setIsPressed: Dispatch<SetStateAction<boolean>>;
}): PressResetControls {
  const { setIsHovered, setIsPressed } = input;
  const pressedAtRef = useRef<number>(0);
  const pressResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingPressReset = useCallback((): void => {
    if (pressResetTimerRef.current) {
      clearTimeout(pressResetTimerRef.current);
      pressResetTimerRef.current = null;
    }
  }, []);

  const releasePress = useCallback((): void => {
    const elapsed = performance.now() - pressedAtRef.current;
    const remaining = Math.max(0, MIN_PRESS_VISIBLE_MS - elapsed);

    if (remaining === 0) {
      setIsPressed(false);
      setIsHovered(false);
      return;
    }

    clearPendingPressReset();
    pressResetTimerRef.current = setTimeout(() => {
      setIsPressed(false);
      setIsHovered(false);
      pressResetTimerRef.current = null;
    }, remaining);
  }, [clearPendingPressReset, setIsHovered, setIsPressed]);

  const beginPress = useCallback((): void => {
    clearPendingPressReset();
    pressedAtRef.current = performance.now();
    setIsPressed(true);
  }, [clearPendingPressReset, setIsPressed]);

  useEffect(() => (): void => clearPendingPressReset(), [clearPendingPressReset]);

  return { beginPress, releasePress, clearPendingPressReset };
}

function useContainerInteractionHandlers(input: UseContainerInteractionHandlersInput): ContainerInteractionHandlers {
  const touchHandlers = useContainerTouchHandlers(input);
  const mouseHandlers = useContainerMouseHandlers(input);

  return useMemo<ContainerInteractionHandlers>(() => ({
    ...touchHandlers,
    ...mouseHandlers,
  }), [mouseHandlers, touchHandlers]);
}

function useContainerTouchHandlers(input: UseContainerInteractionHandlersInput): ContainerTouchHandlers {
  const {
    onClick,
    clickAnimation,
    setIsTouchDevice,
    setIsHovered,
    setIsPressed,
    beginPress,
    releasePress,
    clearPendingPressReset,
  } = input;

  const handleTouchStart = useCallback((): void => {
    setIsTouchDevice(true);

    if (onClick && clickAnimation) {
      beginPress();
    }
  }, [beginPress, clickAnimation, onClick, setIsTouchDevice]);

  const handleTouchEnd = useCallback((): void => {
    releasePress();
  }, [releasePress]);

  const handleTouchCancel = useCallback((): void => {
    clearPendingPressReset();
    setIsPressed(false);
    setIsHovered(false);
  }, [clearPendingPressReset, setIsHovered, setIsPressed]);

  return useMemo<ContainerTouchHandlers>(() => ({
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchCancel,
  }), [handleTouchCancel, handleTouchEnd, handleTouchStart]);
}

function useContainerMouseHandlers(input: UseContainerInteractionHandlersInput): ContainerMouseHandlers {
  const {
    onClick,
    clickAnimation,
    isTouchDevice,
    setIsHovered,
    setIsPressed,
    beginPress,
    releasePress,
    clearPendingPressReset,
  } = input;

  const handleMouseEnter = useCallback((): void => {
    if (onClick && !isTouchDevice) {
      setIsHovered(true);
    }
  }, [isTouchDevice, onClick, setIsHovered]);

  const handleMouseLeave = useCallback((): void => {
    clearPendingPressReset();
    setIsHovered(false);
    setIsPressed(false);
  }, [clearPendingPressReset, setIsHovered, setIsPressed]);

  const handleMouseDown = useCallback((event: MouseEvent<HTMLDivElement>): void => {
    if (isTouchDevice) {
      event.preventDefault();
      return;
    }

    if (onClick && clickAnimation) {
      beginPress();
    }
  }, [beginPress, clickAnimation, isTouchDevice, onClick]);

  const handleMouseUp = useCallback((): void => {
    if (!isTouchDevice) {
      releasePress();
    }
  }, [isTouchDevice, releasePress]);

  return useMemo<ContainerMouseHandlers>(() => ({
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
  }), [handleMouseDown, handleMouseEnter, handleMouseLeave, handleMouseUp]);
}
